import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import { resolvePublicMediaUrl } from '../utils/mediaUrl';
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaDownload } from 'react-icons/fa';
import SEO from '../components/SEO';

function Downloads() {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    try {
      const response = await apiService.downloads.getAll();
      setDownloads(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching downloads:', error);
      setDownloads([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', 'General', 'Academic', 'Exam', 'Sports', 'Events', 'Circulars', 'Results'];
  
  const filteredDownloads = selectedCategory === 'All' 
    ? downloads 
    : downloads.filter(d => d.category === selectedCategory);

  const handleDownload = async (download) => {
    const href = resolvePublicMediaUrl(download.fileUrl);
    if (!href) {
      alert('File not available');
      return;
    }
    try {
      await apiService.downloads.incrementCount(download.id);
    } catch (e) {
      /* count optional */
    }
    const link = document.createElement('a');
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    const parts = String(download.fileUrl || '').split('/');
    link.download = parts[parts.length - 1] || `${download.title || 'download'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileIcon = (type) => {
    switch(type) {
      case 'PDF': return <FaFilePdf className="text-4xl text-red-600" />;
      case 'DOC': 
      case 'DOCX': return <FaFileWord className="text-4xl text-blue-600" />;
      case 'XLS': 
      case 'XLSX': return <FaFileExcel className="text-4xl text-green-600" />;
      case 'JPG': 
      case 'PNG': return <FaFileImage className="text-4xl text-purple-600" />;
      default: return <FaFilePdf className="text-4xl text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="text-center text-xl text-blue-900">Loading downloads...</div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Study Materials & Resources | Vidya Vatika World School"
        description="Download study materials, sample papers, syllabus and other academic resources for all classes at Vidya Vatika Panipat."
        keywords="study material Panipat, sample papers, CBSE resources, downloadable notes Haryana, PDF files, syllabus"
        canonicalUrl="/downloads"
        ogImage="/images/og-image.jpg"
      />
      
      <div className="pt-20 min-h-screen bg-gray-50">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl font-bold font-display mb-4"
          >
            Downloads
          </motion.h1>
          <p className="text-xl text-blue-100">Important documents and resources for students & parents</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <label className="font-semibold text-gray-700">Filter by Category:</label>
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-900">
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
        </div>

        {/* Downloads Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDownloads.map((download) => (
            <div key={download.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-blue-900 mb-2">{download.title}</h3>
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {download.category}
                    </span>
                  </div>
                  <div className="ml-3">
                    {getFileIcon(download.fileType)}
                  </div>
                </div>
                
                {download.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{download.description}</p>
                )}
                
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>📊 {download.downloadCount || 0} downloads</span>
                </div>

                {download.fileUrl ? (
                  <button onClick={() => handleDownload(download)}
                    className="w-full bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition text-center font-semibold flex items-center justify-center gap-2">
                    <FaDownload size={18} /> Download File
                  </button>
                ) : (
                  <button className="w-full bg-gray-300 text-gray-500 py-3 rounded-lg cursor-not-allowed text-center font-semibold">
                    File Not Available
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredDownloads.length === 0 && (
          <div className="text-center text-gray-600 mt-12 py-8">
            <p className="text-xl">No downloads available</p>
            <p className="text-sm mt-2">Please check back later</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default Downloads;
